/**
 * AYU Admin Dashboard — Komplett-Überarbeitung nach MENTO-Vorlage
 * 
 * 4 Haupt-Tabs:
 * 1. Kunden — Liste, Suche, Sortierung, Detail-Ansicht mit Quick Stats
 * 2. Feedback — Micro-Feedback (👍/👎) + NPS-Scores
 * 3. FAQ-Bot — Gespräche, Analytics
 * 4. Korrekturen — Self-Learning mit Badge-Counter
 */
import { useState, useMemo, lazy, Suspense } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  UserPlus,
  Search,
  Shield,
  ShieldOff,
  Eye,
  ArrowLeft,
  Copy,
  Check,
  Loader2,
  RefreshCw,
  BarChart3,
  MessageSquare,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  Star,
  Calendar,
  Mail,
  Activity,
  FileText,
  Heart,
  MessageCircle,
  Mic,
  BookOpen,
  Crown,
  CrownIcon,
  Save,
  Download,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Zap,
  Bug,
  Gift,
  Ticket,
  ToggleLeft,
  ToggleRight,
  Wrench,
  Play,
  CheckCircle2,
  XCircle,
  Music,
  Trash2,
  Info,
  Flame,
  RotateCcw,
  ChevronRight,
  Image,
  AlertCircle,
  CheckSquare,
  Square,
  Send,
  Package,
  X,
  Pencil,
  Smartphone,
  Microscope,
} from "lucide-react";
import { toast } from "sonner";
import AdminBioTab, { ClientBioDetail } from "@/components/admin/AdminBioTab";
import AdminEventTab from "@/components/admin/AdminEventTab";
import ErrorDashboardTab from "@/components/admin/ErrorDashboardTab";
import AppAdminSentry from "./AppAdminSentry";

// ═══════════════════════════════════════════════════════════
//  Design Tokens
// ═══════════════════════════════════════════════════════════

const T = {
  olive: "#6b7f4e",
  oliveLight: "#6b7f4e15",
  oliveMed: "#6b7f4e25",
  dark: "#2d3a1e",
  mid: "#5a6b4a",
  muted: "#8a9680",
  border: "#d4dcc4",
  bg: "#f7f4ef",
  white: "#ffffff",
  saffron: "#c8a45e",
  saffronLight: "#c8a45e15",
};

// ═══════════════════════════════════════════════════════════
//  SPARKLINE SVG — Mini-Diagramm für 7-Tage Aktivität
// ═══════════════════════════════════════════════════════════

function SparklineSvg({ data, color }: { data: number[]; color: string }) {
  const max = Math.max(...data, 1);
  const w = 60;
  const h = 20;
  const padding = 2;
  const hasActivity = data.some((v) => v > 0);

  if (!hasActivity) {
    return (
      <div className="flex items-center gap-1" title="Keine Aktivität in den letzten 7 Tagen">
        <svg width={w} height={h} className="opacity-30">
          <line x1={padding} y1={h - padding} x2={w - padding} y2={h - padding} stroke="#ccc" strokeWidth={1} strokeDasharray="2,2" />
        </svg>
      </div>
    );
  }

  const points = data.map((v, i) => {
    const x = padding + (i / (data.length - 1)) * (w - padding * 2);
    const y = h - padding - (v / max) * (h - padding * 2);
    return `${x},${y}`;
  });

  const areaPoints = [
    `${padding},${h - padding}`,
    ...points,
    `${w - padding},${h - padding}`,
  ].join(" ");

  return (
    <div className="flex items-center gap-1 shrink-0" title={`Letzte 7 Tage: ${data.join(", ")} Nachrichten`}>
      <svg width={w} height={h}>
        <polygon points={areaPoints} fill={color} opacity={0.15} />
        <polyline points={points.join(" ")} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        {/* Dot on last day */}
        {data[data.length - 1] > 0 && (
          <circle cx={w - padding} cy={h - padding - (data[data.length - 1] / max) * (h - padding * 2)} r={2} fill={color} />
        )}
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  TAB 1: KUNDEN
// ═══════════════════════════════════════════════════════════

function ClientList({ onSelectClient }: { onSelectClient: (id: string) => void }) {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "date" | "login" | "activity" | "inactive">("date");
  const [engagementFilter, setEngagementFilter] = useState<"all" | "inactive7" | "inactive30" | "power" | "new" | "telegram_yes" | "telegram_no">("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [bulkEmailSubject, setBulkEmailSubject] = useState("");
  const [bulkEmailBody, setBulkEmailBody] = useState("");
  const [bulkPackageOpen, setBulkPackageOpen] = useState(false);
  const [bulkPackageType, setBulkPackageType] = useState<"basis" | "premium">("premium");
  const [newClient, setNewClient] = useState({
    email: "", firstName: "", lastName: "", password: "", packageType: "basis" as "basis" | "premium",
  });
  const [copiedPw, setCopiedPw] = useState(false);

  const { data: clients, isLoading, refetch } = trpc.admin.listClients.useQuery();

  const bulkEmailMutation = trpc.admin.bulkEmail.useMutation({
    onSuccess: (data) => {
      toast.success(`E-Mail an ${data.sent} Kunden gesendet`);
      setBulkEmailOpen(false); setBulkEmailSubject(""); setBulkEmailBody("");
      setBulkMode(false); setSelectedIds(new Set());
    },
    onError: (err) => toast.error(err.message),
  });
  const bulkPackageMutation = trpc.admin.bulkUpdatePackage.useMutation({
    onSuccess: (data) => {
      toast.success(`Paket für ${data.updated} Kunden geändert`);
      setBulkPackageOpen(false); setBulkMode(false); setSelectedIds(new Set());
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => {
    if (selectedIds.size === sortedAndFiltered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(sortedAndFiltered.map((c) => c.clientId)));
  };

  const createMutation = trpc.clientAuth.register.useMutation({
    onSuccess: () => {
      toast.success("Kunde erfolgreich angelegt");
      refetch();
      setCreateOpen(false);
      setNewClient({ email: "", firstName: "", lastName: "", password: "", packageType: "basis" });
    },
    onError: (err) => toast.error(err.message || "Fehler beim Anlegen"),
  });

  const generatePassword = () => {
    const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$";
    let pw = "";
    for (let i = 0; i < 12; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    setNewClient((prev) => ({ ...prev, password: pw }));
  };

  const sortedAndFiltered = useMemo(() => {
    if (!clients) return [];
    let list = [...clients];

    // Text search
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          c.firstName.toLowerCase().includes(q) ||
          c.lastName.toLowerCase().includes(q) ||
          c.email.toLowerCase().includes(q) ||
          c.clientId.toLowerCase().includes(q)
      );
    }

    // Engagement filter
    const now = Date.now();
    if (engagementFilter === "inactive7") {
      list = list.filter((c) => {
        const last = c.activity?.lastActivityAt ? new Date(c.activity.lastActivityAt).getTime() : (c.lastLoginAt?.getTime() ?? 0);
        return !last || (now - last) > 7 * 86400000;
      });
    } else if (engagementFilter === "inactive30") {
      list = list.filter((c) => {
        const last = c.activity?.lastActivityAt ? new Date(c.activity.lastActivityAt).getTime() : (c.lastLoginAt?.getTime() ?? 0);
        return !last || (now - last) > 30 * 86400000;
      });
    } else if (engagementFilter === "power") {
      list = list.filter((c) => (c.activity?.totalSessions ?? 0) >= 10);
    } else if (engagementFilter === "new") {
      list = list.filter((c) => {
        const created = c.createdAt?.getTime() ?? 0;
        return (now - created) < 7 * 86400000;
      });
    } else if (engagementFilter === "telegram_yes") {
      list = list.filter((c) => c.hasTelegram);
    } else if (engagementFilter === "telegram_no") {
      list = list.filter((c) => !c.hasTelegram);
    }

    // Helper: get last activity timestamp
    const getLastActivity = (c: typeof list[0]) => {
      const actTime = c.activity?.lastActivityAt ? new Date(c.activity.lastActivityAt).getTime() : 0;
      const loginTime = c.lastLoginAt?.getTime() ?? 0;
      return Math.max(actTime, loginTime);
    };

    // Sort (primary)
    list.sort((a, b) => {
      if (sortBy === "name") return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
      if (sortBy === "login") return (b.lastLoginAt?.getTime() ?? 0) - (a.lastLoginAt?.getTime() ?? 0);
      if (sortBy === "activity") return getLastActivity(b) - getLastActivity(a);
      if (sortBy === "inactive") return getLastActivity(a) - getLastActivity(b);
      return (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0);
    });

    // Inactive always at bottom (stable partition)
    const active = list.filter((c) => c.isActive);
    const inactive = list.filter((c) => !c.isActive);
    return [...active, ...inactive];
  }, [clients, search, sortBy, engagementFilter]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-display font-normal text-foreground" >
            Kundenverwaltung
          </h2>
          <p className="text-sm mt-1 text-muted-foreground" >
            {engagementFilter !== "all" || search
              ? `${sortedAndFiltered.length} von ${clients?.length || 0} Kunden`
              : `${clients?.length || 0} Kunden registriert`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm"
            onClick={() => { setBulkMode(!bulkMode); if (bulkMode) setSelectedIds(new Set()); }}
            className={`border-border text-primary hover:text-primary hover:border-primary/40 ${bulkMode ? "bg-primary/10 border-primary/40" : ""}`}
            title="Mehrfachauswahl">
            <CheckSquare className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => {
              if (!sortedAndFiltered.length) { toast.error("Keine Kunden zum Exportieren"); return; }
              const headers = ["Name", "E-Mail", "Paket", "Status", "Admin", "Sessions", "Nachrichten", "Community", "Letzte Aktivität", "Registriert"];
              const rows = sortedAndFiltered.map((c) => {
                const act = c.activity;
                const lastAct = act?.lastActivityAt ? new Date(act.lastActivityAt).toLocaleDateString("de-DE") : "Keine";
                return [
                  `${c.firstName} ${c.lastName}`,
                  c.email,
                  c.packageType || "basis",
                  c.isActive ? "Aktiv" : "Inaktiv",
                  c.isAdmin ? "Ja" : "Nein",
                  String(act?.totalSessions ?? 0),
                  String(act?.totalMessages ?? 0),
                  String(act?.communityPosts ?? 0),
                  lastAct,
                  c.createdAt ? new Date(c.createdAt).toLocaleDateString("de-DE") : "",
                ];
              });
              const bom = "﻿";
              const csv = bom + [headers, ...rows].map((r) => r.map((v) => `"${v.replace(/"/g, '""')}"`).join(";")).join("\n");
              const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url; a.download = `ayu-kunden-${new Date().toISOString().split("T")[0]}.csv`;
              a.click(); URL.revokeObjectURL(url);
              toast.success(`${sortedAndFiltered.length} Kunden exportiert`);
            }}
            className="border-border text-primary hover:text-primary hover:border-primary/40"
            title="Gefilterte Liste als CSV exportieren">
            <Download className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={async () => {
              await refetch();
              toast.success("Kundenliste aktualisiert");
            }}
            className="border-border text-primary hover:text-primary hover:border-primary/40">
            <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
          </Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-primary text-white hover:bg-primary/90" onClick={() => generatePassword()}>
                <UserPlus className="w-4 h-4 mr-2" />
                Neuer Kunde
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border text-foreground">
              <DialogHeader>
                <DialogTitle className="text-primary">Neuen Kunden anlegen</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs mb-1 block text-muted-foreground" >Vorname</label>
                    <Input value={newClient.firstName} onChange={(e) => setNewClient((p) => ({ ...p, firstName: e.target.value }))}
                      className="bg-background border-border text-foreground" placeholder="Vorname" />
                  </div>
                  <div>
                    <label className="text-xs mb-1 block text-muted-foreground" >Nachname</label>
                    <Input value={newClient.lastName} onChange={(e) => setNewClient((p) => ({ ...p, lastName: e.target.value }))}
                      className="bg-background border-border text-foreground" placeholder="Nachname" />
                  </div>
                </div>
                <div>
                  <label className="text-xs mb-1 block text-muted-foreground" >E-Mail</label>
                  <Input type="email" value={newClient.email} onChange={(e) => setNewClient((p) => ({ ...p, email: e.target.value }))}
                    className="bg-background border-border text-foreground" placeholder="email@beispiel.de" />
                </div>
                <div>
                  <label className="text-xs mb-1 block text-muted-foreground" >Passwort</label>
                  <div className="flex gap-2">
                    <Input value={newClient.password} onChange={(e) => setNewClient((p) => ({ ...p, password: e.target.value }))}
                      className="bg-background border-border text-foreground font-mono text-sm" />
                    <Button variant="outline" size="sm" onClick={generatePassword}
                      className="border-border text-primary hover:text-primary shrink-0">Generieren</Button>
                    <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(newClient.password); setCopiedPw(true); setTimeout(() => setCopiedPw(false), 2000); }}
                      className="border-border text-primary hover:text-primary shrink-0">
                      {copiedPw ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="text-xs mb-1 block text-muted-foreground" >Paket</label>
                  <Select value={newClient.packageType} onValueChange={(v) => setNewClient((p) => ({ ...p, packageType: v as "basis" | "premium" }))}>
                    <SelectTrigger className="bg-background border-border text-foreground"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="basis">Basis</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={() => createMutation.mutate(newClient)}
                  disabled={createMutation.isPending || !newClient.email || !newClient.firstName || !newClient.lastName || !newClient.password}
                  className="w-full bg-primary text-white hover:bg-primary/90">
                  {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Kunde anlegen
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search + Sort + Filter */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"  />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Kunden suchen (Name, E-Mail, ID)..."
              className="pl-10 bg-card border-border text-foreground placeholder:text-muted-foreground" />
          </div>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
            <SelectTrigger className="w-full sm:w-[200px] bg-card border-border text-foreground">
              <SelectValue placeholder="Sortierung" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="date">Neueste zuerst</SelectItem>
              <SelectItem value="name">Name A-Z</SelectItem>
              <SelectItem value="login">Letzter Login</SelectItem>
              <SelectItem value="activity">Zuletzt aktiv</SelectItem>
              <SelectItem value="inactive">Inaktivste zuerst</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Engagement Filter Chips */}
        <div className="flex flex-wrap gap-2">
          {[
            { key: "all" as const, label: "Alle" },
            { key: "new" as const, label: "Neu (7 Tage)" },
            { key: "power" as const, label: "Power-User (10+ Sessions)" },
            { key: "inactive7" as const, label: "Inaktiv >7 Tage" },
            { key: "inactive30" as const, label: "Inaktiv >30 Tage" },
            { key: "telegram_yes" as const, label: "Telegram ✅" },
            { key: "telegram_no" as const, label: "Ohne Telegram" },
          ].map((f) => (
            <button key={f.key}
              onClick={() => setEngagementFilter(f.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all border ${
                engagementFilter === f.key
                  ? "bg-primary text-white border-primary"
                  : "bg-card text-primary border-border hover:border-primary/40"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk Action Bar */}
      {bulkMode && (
        <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg border border-primary/30 bg-primary/5">
          <button onClick={selectAll}
            className="flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded hover:bg-primary/10 transition-colors text-primary"
            >
            {selectedIds.size === sortedAndFiltered.length ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
            {selectedIds.size === sortedAndFiltered.length ? "Alle abwählen" : "Alle auswählen"}
          </button>
          <span className="text-xs text-muted-foreground" >{selectedIds.size} ausgewählt</span>
          <div className="flex-1" />
          <Button size="sm" variant="outline" disabled={selectedIds.size === 0}
            onClick={() => setBulkEmailOpen(true)}
            className="border-border text-primary hover:text-primary text-xs h-7">
            <Mail className="w-3.5 h-3.5 mr-1" /> E-Mail senden
          </Button>
          <Button size="sm" variant="outline" disabled={selectedIds.size === 0}
            onClick={() => setBulkPackageOpen(true)}
            className="border-border text-primary hover:text-primary text-xs h-7">
            <Package className="w-3.5 h-3.5 mr-1" /> Paket ändern
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setBulkMode(false); setSelectedIds(new Set()); }}
            className="text-xs h-7 text-muted-foreground hover:text-foreground">
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}

      {/* Bulk Email Dialog */}
      <Dialog open={bulkEmailOpen} onOpenChange={setBulkEmailOpen}>
        <DialogContent className="bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="text-primary">E-Mail an {selectedIds.size} Kunden senden</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground" >
              Die E-Mail wird über Resend an alle ausgewählten Kunden gesendet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Input placeholder="Betreff" value={bulkEmailSubject}
              onChange={(e) => setBulkEmailSubject(e.target.value)}
              className="bg-card border-border" />
            <Textarea placeholder="Nachricht (HTML wird unterstützt)" value={bulkEmailBody}
              onChange={(e) => setBulkEmailBody(e.target.value)}
              className="bg-card border-border min-h-[120px]" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEmailOpen(false)}
              className="border-border text-primary">Abbrechen</Button>
            <Button onClick={() => bulkEmailMutation.mutate({
                clientIds: Array.from(selectedIds), subject: bulkEmailSubject, html: bulkEmailBody,
              })}
              disabled={!bulkEmailSubject.trim() || !bulkEmailBody.trim() || bulkEmailMutation.isPending}
              className="bg-primary text-white hover:bg-primary/90">
              {bulkEmailMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Package Dialog */}
      <Dialog open={bulkPackageOpen} onOpenChange={setBulkPackageOpen}>
        <DialogContent className="bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="text-primary">Paket für {selectedIds.size} Kunden ändern</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Select value={bulkPackageType} onValueChange={(v) => setBulkPackageType(v as "basis" | "premium")}>
              <SelectTrigger className="bg-card border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="basis">Basis</SelectItem>
                <SelectItem value="premium">Premium (DNA)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkPackageOpen(false)}
              className="border-border text-primary">Abbrechen</Button>
            <Button onClick={() => bulkPackageMutation.mutate({
                clientIds: Array.from(selectedIds), packageType: bulkPackageType,
              })}
              disabled={bulkPackageMutation.isPending}
              className="bg-primary text-white hover:bg-primary/90">
              {bulkPackageMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Package className="w-4 h-4 mr-2" />}
              Ändern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client List */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary"  /></div>
      ) : sortedAndFiltered.length === 0 ? (
        <Card className="border-border bg-card">
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground"  />
            <p className="text-muted-foreground">{search ? "Keine Kunden gefunden" : "Noch keine Kunden angelegt"}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {sortedAndFiltered.map((client) => {
            const act = client.activity;
            const lastActive = act?.lastActivityAt
              ? new Date(act.lastActivityAt)
              : client.lastLoginAt
                ? new Date(client.lastLoginAt)
                : null;
            const daysSince = lastActive
              ? Math.floor((Date.now() - lastActive.getTime()) / 86400000)
              : null;
            const activityColor = daysSince === null ? T.muted : daysSince <= 1 ? "#22c55e" : daysSince <= 7 ? "#c8a45e" : "#ef4444";

            return (
              <Card key={client.clientId}
                className={`border-border hover:border-primary/25 transition-all cursor-pointer group ${!client.isActive ? "bg-gray-50 opacity-60" : "bg-card"} ${selectedIds.has(client.clientId) ? "ring-2 ring-primary/40 bg-primary/5" : ""}`}
                onClick={() => bulkMode ? toggleSelect(client.clientId) : onSelectClient(client.clientId)}>
                <CardContent className="p-4">
                  {/* Row 1: Name + Badges */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {bulkMode ? (
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 border-2 transition-colors ${selectedIds.has(client.clientId) ? "border-primary bg-primary/10" : "border-border bg-transparent"}`}>
                          {selectedIds.has(client.clientId) ? <Check className="w-5 h-5 text-primary" /> : <Square className="w-4 h-4 text-border" />}
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shrink-0 bg-primary/10 text-primary">
                          {client.firstName[0]}{client.lastName[0]}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-medium truncate text-foreground" >
                          {client.firstName} {client.lastName}
                        </p>
                        <p className="text-xs truncate text-muted-foreground" >{client.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Badge variant="outline" className={
                        `text-[10px] py-0 ${client.packageType === "premium"
                          ? "border-primary/40 text-primary bg-primary/10"
                          : "border-muted-foreground/40 text-primary"}`
                      }>
                        {client.packageType || "basis"}
                      </Badge>
                      {client.isActive ? (
                        <Badge variant="outline" className="border-green-500/40 text-green-600 bg-green-500/10 text-[10px] py-0">Aktiv</Badge>
                      ) : (
                        <Badge variant="outline" className="border-red-500/40 text-red-600 bg-red-500/10 text-[10px] py-0">Inaktiv</Badge>
                      )}
                      {client.isAdmin && (
                        <Badge variant="outline" className="border-amber-500/40 text-amber-700 bg-amber-500/10 text-[10px] py-0">Admin</Badge>
                      )}
                      {client.hasTelegram && (
                        <span title="Telegram verbunden" className="flex items-center">
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" fill="#0088cc"/>
                          </svg>
                        </span>
                      )}
                      <Eye className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground"  />
                    </div>
                  </div>

                  {/* Row 2: Activity Preview + Sparkline */}
                  <div className="flex items-center justify-between mt-2.5 pl-[52px]">
                    <div className="flex items-center gap-4 text-[11px] text-muted-foreground" >
                      <span className="flex items-center gap-1" title="Sessions">
                        <MessageSquare className="w-3 h-3 text-primary"  />
                        {act?.totalSessions ?? 0} Sessions
                      </span>
                      <span className="flex items-center gap-1" title="Nachrichten">
                        <MessageCircle className="w-3 h-3" style={{ color: "#4a7c8a" }} />
                        {act?.totalMessages ?? 0} Nachr.
                      </span>
                      <span className="hidden sm:flex items-center gap-1" title="Community">
                        <Users className="w-3 h-3" style={{ color: "#4a8a6b" }} />
                        {act?.communityPosts ?? 0}
                      </span>
                      <span className="hidden sm:flex items-center gap-1" title="Letzte Aktivität">
                        <Activity className="w-3 h-3" style={{ color: activityColor }} />
                        {daysSince === null
                          ? "Keine"
                          : daysSince === 0
                            ? "Heute"
                            : daysSince === 1
                              ? "Gestern"
                              : `${daysSince}d`}
                      </span>
                    </div>
                    {/* Sparkline: 7-Tage Mini-Diagramm */}
                    <SparklineSvg data={client.sparkline} color={activityColor} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  CLIENT DETAIL VIEW — Quick Stats + Verwaltung
// ═══════════════════════════════════════════════════════════

function ClientDetail({ clientId, onBack }: { clientId: string; onBack: () => void }) {
  const [notes, setNotes] = useState<string | null>(null);
  const [notesEditing, setNotesEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [reportDateFrom, setReportDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [reportDateTo, setReportDateTo] = useState(() => new Date().toISOString().split("T")[0]);
  const [mikrobiomExpanded, setMikrobiomExpanded] = useState(false);

  const { data: client, isLoading, refetch } = trpc.admin.getClientDetails.useQuery({ clientId });
  const { data: activity } = trpc.admin.getClientActivity.useQuery({ clientId });
  const { data: premiumStatus, refetch: refetchPremium } = trpc.stripe.getPremiumStatus.useQuery({ clientId });

  const toggleActiveMutation = trpc.admin.toggleActive.useMutation({
    onSuccess: () => { toast.success(client?.isActive ? "Kunde deaktiviert" : "Kunde aktiviert"); refetch(); },
  });
  const updatePackageMutation = trpc.admin.updatePackage.useMutation({
    onSuccess: () => { toast.success("Paket aktualisiert"); refetch(); },
  });
  const updateNotesMutation = trpc.admin.updateNotes.useMutation({
    onSuccess: () => { toast.success("Notizen gespeichert"); setNotesEditing(false); refetch(); },
  });
  const grantPremiumMutation = trpc.stripe.grantPremium.useMutation({
    onSuccess: () => { toast.success("Premium-Zugang gewährt"); refetchPremium(); },
  });
  const revokePremiumMutation = trpc.stripe.revokePremium.useMutation({
    onSuccess: () => { toast.success("Premium-Zugang entzogen"); refetchPremium(); },
  });
  const generateReportMutation = trpc.pdfExport.generateReport.useMutation({
    onSuccess: (data) => {
      if (data.success && data.htmlUrl) {
        window.open(data.htmlUrl, "_blank");
        toast.success(`Bericht erstellt (${data.diaryCount} Einträge, ${data.summaryCount} Zusammenfassungen)`);
      } else {
        toast.error("error" in data ? (data as any).error : "Bericht konnte nicht erstellt werden");
      }
    },
  });
  const deleteClientMutation = trpc.admin.deleteClient.useMutation({
    onSuccess: (data) => {
      toast.success(`Kunde komplett gelöscht (${data.deletedTables.length} Tabellen bereinigt)`);
      setDeleteDialogOpen(false);
      onBack();
    },
    onError: (err) => toast.error(err.message || "Fehler beim Löschen"),
  });
  const toggleAdminMutation = trpc.admin.toggleAdmin.useMutation({
    onSuccess: (data) => {
      toast.success(data.isAdmin ? "Admin-Rechte gewährt" : "Admin-Rechte entzogen");
      refetch();
    },
    onError: (err) => toast.error(err.message || "Fehler beim Ändern der Admin-Rechte"),
  });
  const [tgMsgOpen, setTgMsgOpen] = useState(false);
  const [tgMsgText, setTgMsgText] = useState("");
  const sendTgMutation = trpc.admin.sendTelegramMessage.useMutation({
    onSuccess: () => { toast.success("Telegram-Nachricht gesendet"); setTgMsgOpen(false); setTgMsgText(""); },
    onError: (err) => toast.error(err.message || "Nachricht konnte nicht gesendet werden"),
  });

  // Initialize notes from client data
  if (client && notes === null) {
    setNotes(String(client.adminNotes || ""));
  }

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary"  /></div>;
  if (!client) return (
    <div className="text-center py-12">
      <p className="text-muted-foreground">Kunde nicht gefunden</p>
      <Button variant="outline" onClick={onBack} className="mt-4 border-border text-primary">Zurück</Button>
    </div>
  );

  const doshaProfile = client.doshaProfile as { primary?: string; secondary?: string; phenotypeId?: string } | null;

  const quickStats = [
    { icon: BookOpen, label: "Tagebuch", value: activity?.chatSessionsTagebuch ?? 0, color: "#6b7f4e" },
    { icon: MessageCircle, label: "Annam", value: activity?.chatSessionsAyu ?? 0, color: "#4a7c8a" },
    { icon: Mic, label: "Voice", value: activity?.chatSessionsVoice ?? 0, color: "#8a6b4a" },
    { icon: MessageSquare, label: "Nachrichten", value: activity?.messagesTotal ?? 0, color: "#6b4a8a" },
    { icon: Users, label: "Community", value: activity?.communityMessages ?? 0, color: "#4a8a6b" },
    { icon: ThumbsUp, label: "Feedback", value: activity?.feedbackGiven ?? 0, sub: activity ? `${activity.feedbackPositive}↑ ${activity.feedbackNegative}↓` : undefined, color: "#c8a45e" },
    { icon: Star, label: "NPS", value: activity?.npsResponses ?? 0, color: "#e07b4a" },
    { icon: FileText, label: "Dokumente", value: activity?.documentsUploaded ?? 0, color: "#7b4ae0" },
  ];

  return (
    <div className="space-y-4">
      {/* Header — kompakt */}
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={onBack} className="border-border text-primary hover:text-primary h-8 w-8 shrink-0">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg truncate font-display font-normal text-foreground" >
              {client.firstName} {client.lastName}
            </h2>
            {client.isActive ? (
              <Badge variant="outline" className="border-green-500/40 text-green-600 bg-green-500/10 text-[10px] py-0">Aktiv</Badge>
            ) : (
              <Badge variant="outline" className="border-red-500/40 text-red-600 bg-red-500/10 text-[10px] py-0">Inaktiv</Badge>
            )}
            <Badge variant="outline" className={
              `text-[10px] py-0 ${client.packageType === "premium"
                ? "border-primary/40 text-primary bg-primary/10"
                : "border-muted-foreground/40 text-primary"}`
            }>
              {client.packageType}
            </Badge>
            {client.isAdmin && (
              <Badge variant="outline" className="border-amber-500/40 text-amber-700 bg-amber-500/10 text-[10px] py-0">Admin</Badge>
            )}
          </div>
          <p className="text-[11px] truncate text-muted-foreground" >{client.email} · {client.clientId}</p>
        </div>
      </div>

      {/* Quick Stats Grid — kompakt */}
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
        {quickStats.map((stat) => (
          <div key={stat.label} className="flex flex-col items-center p-2 rounded-lg border border-border bg-card">
            <stat.icon className="w-3.5 h-3.5 mb-1" style={{ color: stat.color }} />
            <p className="text-sm font-bold leading-none text-foreground" >{stat.value}</p>
            <p className="text-[9px] leading-tight text-center mt-0.5 text-muted-foreground" >
              {stat.label}
            </p>
          </div>
        ))}
      </div>

      {/* Management Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Account Info */}
        <Card className="border-border bg-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-primary" >Account-Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] text-muted-foreground" >Registriert</p>
                <p className="text-sm text-foreground" >
                  {client.createdAt ? new Date(client.createdAt).toLocaleDateString("de-DE") : "—"}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground" >Letzter Login</p>
                <p className="text-sm text-foreground" >
                  {client.lastLoginAt ? new Date(client.lastLoginAt).toLocaleDateString("de-DE") : "—"}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground" >Onboarding</p>
                <p className="text-sm text-foreground" >
                  {client.onboardingComplete ? "Abgeschlossen" : "Ausstehend"}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground" >Coaching-Stil</p>
                <p className="text-sm text-foreground" >{client.coachingStyle || "—"}</p>
              </div>
            </div>
            {/* Letzte Aktivität */}
            {activity?.lastActivityAt && (
              <div>
                <p className="text-[10px] text-muted-foreground" >Letzte Aktivität</p>
                <p className="text-sm text-foreground" >
                  {new Date(activity.lastActivityAt).toLocaleDateString("de-DE", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            )}
            {/* Telegram-Status */}
            <div className="pt-2 border-t border-border">
              <p className="text-[10px] mb-1.5 text-muted-foreground" >Telegram</p>
              {client.telegram ? (
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1.5">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" fill="#0088cc"/>
                    </svg>
                    <span className="text-sm font-medium" style={{ color: "#0088cc" }}>Verbunden</span>
                  </span>
                  {client.telegram.username && (
                    <span className="text-xs text-muted-foreground" >@{client.telegram.username}</span>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 opacity-40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" fill="#999"/>
                  </svg>
                  <span className="text-sm text-muted-foreground" >Nicht verbunden</span>
                </div>
              )}
              {client.telegram && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {client.telegram.checkinsEnabled && (
                    <Badge variant="outline" className="text-[10px] py-0 border-sky-400/40 text-sky-600 bg-sky-50">Check-ins aktiv</Badge>
                  )}
                  {client.telegram.eveningReminderEnabled && (
                    <Badge variant="outline" className="text-[10px] py-0 border-violet-400/40 text-violet-600 bg-violet-50">Abend-Erinnerung</Badge>
                  )}
                  <Badge variant="outline" className="text-[10px] py-0 border-muted-foreground/30 text-primary">
                    Chat: {client.telegram.defaultChatType === "tagebuch" ? "Tagebuch" : "Annam"}
                  </Badge>
                </div>
              )}
              {/* Telegram Nachricht senden */}
              {client.telegram && (
                <div className="mt-3">
                  {!tgMsgOpen ? (
                    <button
                      onClick={() => setTgMsgOpen(true)}
                      className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md border transition-colors hover:bg-sky-50"
                      style={{ color: "#0088cc", borderColor: "#0088cc40" }}
                    >
                      <Send className="w-3 h-3" />
                      Nachricht senden
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <textarea
                        value={tgMsgText}
                        onChange={(e) => setTgMsgText(e.target.value)}
                        placeholder="Nachricht an Telegram..."
                        className="w-full text-sm rounded-md border p-2 resize-none focus:outline-none focus:ring-1"
                        style={{ borderColor: "#0088cc40", minHeight: "80px" }}
                        rows={3}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => sendTgMutation.mutate({ clientId: client.clientId, message: tgMsgText })}
                          disabled={!tgMsgText.trim() || sendTgMutation.isPending}
                          className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md text-white transition-colors disabled:opacity-50"
                          style={{ backgroundColor: "#0088cc" }}
                        >
                          {sendTgMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                          Senden
                        </button>
                        <button
                          onClick={() => { setTgMsgOpen(false); setTgMsgText(""); }}
                          className="text-xs px-3 py-1.5 rounded-md border border-border transition-colors hover:bg-gray-50 text-muted-foreground"
                        >
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Paket & Status */}
        <Card className="border-border bg-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-primary" >Paket & Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-[10px] mb-2 text-muted-foreground" >Paket</p>
              <Select value={client.packageType || "basis"}
                onValueChange={(v) => updatePackageMutation.mutate({ clientId: client.clientId, packageType: v as "basis" | "premium" })}>
                <SelectTrigger className="bg-background border-border text-foreground"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="basis">AYU Basis</SelectItem>
                  <SelectItem value="premium">AYU Premium</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-[10px] mb-2 text-muted-foreground" >Konto-Status</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm"
                  onClick={() => toggleActiveMutation.mutate({ clientId: client.clientId, isActive: !client.isActive })}
                  disabled={toggleActiveMutation.isPending}
                  className={client.isActive
                    ? "border-red-500/30 text-red-600 hover:bg-red-500/10 flex-1"
                    : "border-green-500/30 text-green-600 hover:bg-green-500/10 flex-1"}>
                  {client.isActive ? <><ShieldOff className="w-4 h-4 mr-2" />Deaktivieren</> : <><Shield className="w-4 h-4 mr-2" />Aktivieren</>}
                </Button>
                <Dialog open={deleteDialogOpen} onOpenChange={(open) => { setDeleteDialogOpen(open); if (!open) setDeleteConfirmText(""); }}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="border-red-600/40 text-red-700 hover:bg-red-600/10">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-red-300 text-foreground">
                    <DialogHeader>
                      <DialogTitle className="text-red-700 flex items-center gap-2">
                        <Trash2 className="w-5 h-5" />
                        Kunden endgültig löschen
                      </DialogTitle>
                      <DialogDescription className="text-primary pt-2">
                        <strong className="text-red-700">Achtung:</strong> Diese Aktion löscht <strong>{client.firstName} {client.lastName}</strong> und
                        alle zugehörigen Daten (Chats, Tagebuch, Rezepte, Punkte, Abzeichen, Abonnements, etc.) unwiderruflich.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 mt-2">
                      <p className="text-sm text-muted-foreground" >
                        Bitte tippe <strong className="font-mono text-red-700">LÖSCHEN</strong> ein, um zu bestätigen:
                      </p>
                      <Input
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder="LÖSCHEN"
                        className="bg-red-50 border-red-300 text-foreground font-mono"
                      />
                    </div>
                    <DialogFooter className="mt-4 gap-2">
                      <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setDeleteConfirmText(""); }}
                        className="border-border text-primary">
                        Abbrechen
                      </Button>
                      <Button
                        onClick={() => deleteClientMutation.mutate({ clientId: client.clientId })}
                        disabled={deleteConfirmText !== "LÖSCHEN" || deleteClientMutation.isPending}
                        className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                        {deleteClientMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                        Endgültig löschen
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            <div>
              <p className="text-[10px] mb-2 text-muted-foreground" >Admin-Rechte</p>
              <Button variant="outline" size="sm"
                onClick={() => toggleAdminMutation.mutate({ clientId: client.clientId, isAdmin: !client.isAdmin })}
                disabled={toggleAdminMutation.isPending}
                className={client.isAdmin
                  ? "border-amber-500/30 text-amber-700 hover:bg-amber-500/10 w-full"
                  : "border-border text-primary hover:bg-[#f0f3eb] w-full"}>
                {toggleAdminMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                {client.isAdmin ? "Admin-Rechte entziehen" : "Zum Admin machen"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Premium Audio Access */}
      <PremiumAccessCard
        premiumStatus={premiumStatus}
        clientId={clientId}
        grantPremiumMutation={grantPremiumMutation}
        revokePremiumMutation={revokePremiumMutation}
      />

      {/* Therapeuten-Bericht */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 text-primary" >
            <FileText className="w-4 h-4" />
            Therapeuten-Bericht
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="text-[10px] block mb-1 text-muted-foreground" >Von</label>
              <Input type="date" value={reportDateFrom} onChange={(e) => setReportDateFrom(e.target.value)}
                className="bg-background border-border text-foreground text-sm" />
            </div>
            <div className="flex-1">
              <label className="text-[10px] block mb-1 text-muted-foreground" >Bis</label>
              <Input type="date" value={reportDateTo} onChange={(e) => setReportDateTo(e.target.value)}
                className="bg-background border-border text-foreground text-sm" />
            </div>
            <Button size="sm" className="bg-primary text-white hover:bg-primary/90"
              onClick={() => generateReportMutation.mutate({ clientId, dateFrom: reportDateFrom, dateTo: reportDateTo })}
              disabled={generateReportMutation.isPending}>
              {generateReportMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
              Bericht erstellen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Admin-Notizen */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm text-primary" >Betreuer-Notizen</CardTitle>
            {!notesEditing && (
              <Button variant="ghost" size="sm" onClick={() => setNotesEditing(true)}
                className="text-primary hover:text-primary h-7 px-2 text-xs">
                Bearbeiten
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {notesEditing ? (
            <div className="space-y-2">
              <Textarea value={notes || ""} onChange={(e) => setNotes(e.target.value)}
                placeholder="Notizen zum Kunden..."
                className="bg-background border-border text-foreground min-h-[100px]" />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => { setNotesEditing(false); setNotes(String(client.adminNotes || "")); }}
                  className="border-border text-primary">Abbrechen</Button>
                <Button size="sm" className="bg-primary text-white hover:bg-primary/90"
                  onClick={() => updateNotesMutation.mutate({ clientId, notes: notes || "" })}
                  disabled={updateNotesMutation.isPending}>
                  {updateNotesMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Speichern
                </Button>
              </div>
            </div>
          ) : (
            <p className={`text-sm whitespace-pre-wrap ${notes ? "text-foreground" : "text-muted-foreground"}`}>
              {notes || "Keine Notizen vorhanden."}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Phänotyp */}
      {doshaProfile?.primary && (
        <Card className="border-border" style={{ background: "linear-gradient(135deg, #6b7f4e15, #c8a45e15)" }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-primary" >Phänotyp-Ergebnis</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-light text-foreground" >
              {doshaProfile.primary}
              {doshaProfile.secondary && <span className="text-primary"> / {doshaProfile.secondary}</span>}
            </p>
            {doshaProfile.phenotypeId && <p className="text-xs mt-1 text-muted-foreground" >Typ-ID: {doshaProfile.phenotypeId}</p>}
          </CardContent>
        </Card>
      )}

      {/* Mikrobiom-Daten */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-2 cursor-pointer" onClick={() => setMikrobiomExpanded(!mikrobiomExpanded)}>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2 text-primary" >
              <Microscope className="w-4 h-4" />
              Mikrobiom-Daten
            </CardTitle>
            {mikrobiomExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground"  /> : <ChevronDown className="w-4 h-4 text-muted-foreground"  />}
          </div>
        </CardHeader>
        {mikrobiomExpanded && (
          <CardContent>
            <ClientBioDetail
              clientId={clientId}
              clientName={`${client.firstName} ${client.lastName}`}
              embedded={true}
            />
          </CardContent>
        )}
      </Card>

      {/* Anamnese Data */}
      {client.anamneseData && Object.keys(client.anamneseData as Record<string, unknown>).length > 0 && (
        <AnamneseSection data={client.anamneseData as Record<string, unknown>} />
      )}
    </div>
  );
}

function PremiumAccessCard({ premiumStatus, clientId, grantPremiumMutation, revokePremiumMutation }: {
  premiumStatus: any;
  clientId: string;
  grantPremiumMutation: any;
  revokePremiumMutation: any;
}) {
  const ps = premiumStatus as { hasAccess?: boolean; plan?: string | null; sessionsUsed?: number; sessionsLimit?: number | null } | undefined;
  return (
    <Card className="border-border bg-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-primary" >
          <Crown className="w-4 h-4" />
          Premium Audio Sessions
        </CardTitle>
      </CardHeader>
      <CardContent>
        {ps?.hasAccess ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/10">
              <Zap className="w-5 h-5 text-primary"  />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground" >
                  Aktiv: {String(ps.plan) === "admin_grant" ? "Admin-Grant" : String(ps.plan) === "premium_package" ? "Premium-Paket (manuell)" : String(ps.plan || "").toUpperCase()}
                </p>
                <p className="text-xs text-muted-foreground" >
                  {Number(ps.sessionsLimit) > 0
                    ? `${Number(ps.sessionsUsed)} / ${Number(ps.sessionsLimit)} Sessions genutzt`
                    : "Unbegrenzte Sessions"}
                </p>
              </div>
              <Button variant="outline" size="sm"
                onClick={() => revokePremiumMutation.mutate({ clientId })}
                disabled={revokePremiumMutation.isPending}
                className="border-red-500/30 text-red-600 hover:bg-red-500/10">
                Entziehen
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground" >Kein aktiver Premium-Zugang.</p>
            <div className="flex gap-2 flex-wrap">
              {(["starter", "medium", "unlimited"] as const).map((plan) => (
                <Button key={plan} variant="outline" size="sm"
                  onClick={() => grantPremiumMutation.mutate({ clientId, plan })}
                  disabled={grantPremiumMutation.isPending}
                  className="border-primary/30 text-primary hover:bg-primary/10">
                  <Crown className="w-3 h-3 mr-1" />
                  {plan === "starter" ? "Starter (10)" : plan === "medium" ? "Medium (25)" : "Unlimited"}
                </Button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AnamneseSection({ data }: { data: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card className="border-border bg-card">
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-primary" >Fragebogen-Daten</CardTitle>
          {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground"  /> : <ChevronDown className="w-4 h-4 text-muted-foreground"  />}
        </div>
      </CardHeader>
      {expanded && (
        <CardContent>
          <pre className="text-xs p-4 rounded-lg overflow-auto max-h-64 text-muted-foreground bg-background">
            {JSON.stringify(data, null, 2)}
          </pre>
        </CardContent>
      )}
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════
//  TAB 2: FEEDBACK
// ═══════════════════════════════════════════════════════════

function FeedbackTab() {
  const { data, isLoading, refetch } = trpc.feedback.adminStats.useQuery();

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary"  /></div>;
  if (!data) return <p className="text-muted-foreground">Keine Daten verfügbar.</p>;

  const { stats, recent, nps } = data;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-display font-normal text-foreground" >
          Feedback & NPS
        </h2>
        <Button variant="outline" size="sm" onClick={() => refetch()}
          className="border-border text-primary hover:text-primary">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-border bg-card">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-light text-primary" >{stats.total}</p>
            <p className="text-xs mt-1 text-muted-foreground" >Gesamt</p>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-light text-green-600">{stats.positive}</p>
            <p className="text-xs mt-1 text-muted-foreground" >Positiv</p>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-light text-red-500">{stats.negative}</p>
            <p className="text-xs mt-1 text-muted-foreground" >Negativ</p>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-light text-[#c8a45e]">{stats.rate}%</p>
            <p className="text-xs mt-1 text-muted-foreground" >Zufriedenheit</p>
          </CardContent>
        </Card>
      </div>

      {/* Satisfaction Bar */}
      {stats.total > 0 && (
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <p className="text-xs mb-2 text-muted-foreground" >Zufriedenheitsrate</p>
            <div className="w-full h-4 rounded-full overflow-hidden" style={{ background: "#fee2e2" }}>
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${stats.rate}%`, background: "#22c55e" }} />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-green-600">{stats.positive} positiv</span>
              <span className="text-[10px] text-red-500">{stats.negative} negativ</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Feedback */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-primary" >Letztes Feedback</CardTitle>
        </CardHeader>
        <CardContent>
          {recent.length === 0 ? (
            <p className="text-sm text-muted-foreground" >Noch kein Feedback erhalten.</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {recent.slice(0, 30).map((fb) => (
                <div key={fb.id} className="flex items-start gap-3 p-3 rounded-lg bg-background">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${fb.rating === 1 ? "bg-green-500/15" : "bg-red-500/15"}`}>
                    {fb.rating === 1 ? <ThumbsUp className="w-4 h-4 text-green-600" /> : <ThumbsDown className="w-4 h-4 text-red-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-foreground" >{fb.clientId}</span>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-border">{fb.sessionType}</Badge>
                      {fb.context && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-primary/30 text-primary">{fb.context}</Badge>
                      )}
                    </div>
                    {fb.comment && <p className="text-xs mt-1 text-muted-foreground" >{fb.comment}</p>}
                    <p className="text-[10px] mt-1 text-muted-foreground" >
                      {new Date(fb.createdAt).toLocaleDateString("de-DE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* NPS Responses */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-primary" >NPS-Bewertungen</CardTitle>
        </CardHeader>
        <CardContent>
          {nps.length === 0 ? (
            <p className="text-sm text-muted-foreground" >Noch keine NPS-Bewertungen.</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {nps.slice(0, 20).map((n) => (
                <div key={n.id} className="flex items-start gap-3 p-3 rounded-lg bg-background">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-bold text-sm"
                    style={{
                      background: n.score >= 9 ? "#22c55e15" : n.score >= 7 ? "#c8a45e15" : "#ef444415",
                      color: n.score >= 9 ? "#22c55e" : n.score >= 7 ? "#c8a45e" : "#ef4444",
                    }}>
                    {n.score}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-foreground" >{n.clientId}</span>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-border">{n.milestone}</Badge>
                    </div>
                    {n.comment && <p className="text-xs mt-1 text-muted-foreground" >{n.comment}</p>}
                    <p className="text-[10px] mt-1 text-muted-foreground" >
                      {new Date(n.createdAt).toLocaleDateString("de-DE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  TAB 3: FAQ-BOT
// ═══════════════════════════════════════════════════════════

function FaqMessageBubble({ msg, prevUserMsg }: { msg: any; prevUserMsg?: any }) {
  const [editing, setEditing] = useState(false);
  const [correction, setCorrection] = useState("");
  const utils = trpc.useUtils();

  const updateMutation = trpc.faq.updateCorrection.useMutation({
    onSuccess: () => {
      toast.success("Korrektur gespeichert — der Bot lernt dazu!");
      setEditing(false);
      setCorrection("");
      utils.faq.getConversationMessages.invalidate();
      utils.faq.getPendingCorrections.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const createCorrectionMutation = trpc.faq.createInlineCorrection.useMutation({
    onSuccess: () => {
      toast.success("Korrektur gespeichert — der Bot lernt dazu!");
      setEditing(false);
      setCorrection("");
      utils.faq.getPendingCorrections.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const isLowConfidence = msg.role === "assistant" && msg.confidence === "low";
  const isMediumConfidence = msg.role === "assistant" && msg.confidence === "medium";
  const isAssistant = msg.role === "assistant";

  return (
    <div className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] p-3 rounded-lg text-sm relative group ${
        msg.role === "user"
          ? "bg-primary/10 text-foreground"
          : isLowConfidence
            ? "bg-red-50 border-2 border-red-300 text-foreground"
            : isMediumConfidence
              ? "bg-yellow-50 border border-yellow-200 text-foreground"
              : "bg-background text-foreground"
      }`}>
        {/* ROT-Markierung Label für unsichere Antworten */}
        {isLowConfidence && (
          <div className="flex items-center gap-1 mb-2 text-red-600">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span className="text-[10px] font-bold uppercase tracking-wide">Unsichere Antwort — Korrektur empfohlen</span>
          </div>
        )}

        <p className="whitespace-pre-wrap">{msg.content}</p>

        <div className="flex items-center gap-2 mt-2">
          <span className="text-[10px] text-muted-foreground" >
            {new Date(msg.createdAt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
          </span>
          {msg.confidence && (
            <Badge variant="outline" className={`text-[10px] px-1 py-0 ${
              msg.confidence === "high" ? "border-green-500/40 text-green-600" :
              msg.confidence === "medium" ? "border-yellow-500/40 text-yellow-600" :
              "border-red-500/40 text-red-600 bg-red-50"
            }`}>
              {msg.confidence === "high" ? "Sicher" : msg.confidence === "medium" ? "Mittel" : "Unsicher"}
            </Badge>
          )}
          {msg.intent && (
            <Badge variant="outline" className="text-[10px] px-1 py-0 border-border text-muted-foreground">
              {msg.intent}
            </Badge>
          )}

          {/* Inline-Korrektur-Button für Bot-Nachrichten — immer sichtbar */}
          {isAssistant && !editing && (
            <button
              onClick={(e) => { e.stopPropagation(); setEditing(true); setCorrection(""); }}
              className="text-[10px] text-primary/60 hover:text-[#5a6e3f] hover:underline transition-colors ml-auto flex items-center gap-1"
            >
              <Pencil className="w-3 h-3" />
              Korrigieren
            </button>
          )}
        </div>

        {/* Inline-Korrektur-Eingabe */}
        {editing && (
          <div className="mt-3 pt-3 border-t border-border space-y-2">
            <p className="text-[10px] font-medium text-primary" >Bessere Antwort eingeben:</p>
            <Textarea
              value={correction}
              onChange={(e) => setCorrection(e.target.value)}
              placeholder="Die richtige Antwort eingeben..."
              className="bg-card border-border text-foreground min-h-[60px] text-xs"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setEditing(false); }}
                className="border-border text-primary h-7 text-xs">Abbrechen</Button>
              <Button size="sm" className="bg-primary text-white hover:bg-primary/90 h-7 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  createCorrectionMutation.mutate({
                    messageId: msg.id,
                    originalQuestion: prevUserMsg?.content || "(Unbekannte Frage)",
                    originalAnswer: msg.content,
                    correctedAnswer: correction,
                  });
                }}
                disabled={createCorrectionMutation.isPending || !correction.trim()}>
                {createCorrectionMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                Speichern
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FaqBotTab() {
  const [selectedConversation, setSelectedConversation] = useState<number | null>(null);
  const [botFilter, setBotFilter] = useState<"homepage" | "app" | undefined>(undefined);

  const { data: conversations, isLoading, refetch } = trpc.faq.getConversations.useQuery(
    botFilter ? { botType: botFilter, limit: 100 } : { limit: 100 }
  );
  const { data: messages } = trpc.faq.getConversationMessages.useQuery(
    { conversationId: selectedConversation! },
    { enabled: !!selectedConversation }
  );

  // Count low-confidence messages per conversation for visual indicator
  const lowConfidenceCount = useMemo(() => {
    if (!messages) return 0;
    return messages.filter((m) => m.confidence === "low").length;
  }, [messages]);

  if (selectedConversation && messages) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => setSelectedConversation(null)}
            className="border-border text-primary hover:text-primary">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h3 className="text-lg font-light text-foreground" >Gespräch #{selectedConversation}</h3>
          {lowConfidenceCount > 0 && (
            <Badge className="bg-red-500 text-white text-[10px]">
              {lowConfidenceCount} unsichere Antwort{lowConfidenceCount > 1 ? "en" : ""}
            </Badge>
          )}
        </div>
        <div className="space-y-3 max-h-[600px] overflow-y-auto p-1">
          {messages.map((msg, idx) => {
            // Find the previous user message for context
            const prevUserMsg = msg.role === "assistant"
              ? messages.slice(0, idx).reverse().find((m) => m.role === "user")
              : undefined;
            return <FaqMessageBubble key={msg.id} msg={msg} prevUserMsg={prevUserMsg} />;
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-2xl font-display font-normal text-foreground" >
          FAQ-Bot Gespräche
        </h2>
        <div className="flex gap-2">
          <Select value={botFilter || "all"} onValueChange={(v) => setBotFilter(v === "all" ? undefined : v as "homepage" | "app")}>
            <SelectTrigger className="w-[150px] bg-card border-border text-foreground"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="all">Alle Bots</SelectItem>
              <SelectItem value="homepage">Homepage-Bot</SelectItem>
              <SelectItem value="app">App-Bot</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => refetch()}
            className="border-border text-primary hover:text-primary">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary"  /></div>
      ) : !conversations || conversations.length === 0 ? (
        <Card className="border-border bg-card">
          <CardContent className="p-8 text-center">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 text-muted-foreground"  />
            <p className="text-muted-foreground">Noch keine FAQ-Gespräche.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {conversations.map((conv) => (
            <Card key={conv.id}
              className="border-border bg-card hover:border-primary/25 transition-all cursor-pointer"
              onClick={() => setSelectedConversation(conv.id)}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${conv.botType === "homepage" ? "bg-[#4a7c8a15]" : "bg-primary/10"}`}>
                    <MessageSquare className={`w-4 h-4 ${conv.botType === "homepage" ? "text-[#4a7c8a]" : "text-primary"}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground" >
                      {conv.name || conv.clientId || conv.visitorId?.substring(0, 8) || "Anonym"}
                    </p>
                    <p className="text-[10px] text-muted-foreground" >
                      {conv.messageCount} Nachrichten · {conv.startPage || "—"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-[10px] ${conv.botType === "homepage" ? "border-blue-500/40 text-blue-600" : "border-primary/40 text-primary"}`}>
                    {conv.botType}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground" >
                    {new Date(conv.createdAt).toLocaleDateString("de-DE", { day: "numeric", month: "short" })}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  TAB 4: KORREKTUREN (Self-Learning)
// ═══════════════════════════════════════════════════════════

function CorrectionsTab() {
  const { data: corrections, isLoading, refetch } = trpc.faq.getPendingCorrections.useQuery();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [correctedAnswer, setCorrectedAnswer] = useState("");

  const updateMutation = trpc.faq.updateCorrection.useMutation({
    onSuccess: () => {
      toast.success("Korrektur gespeichert");
      setEditingId(null);
      setCorrectedAnswer("");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-display font-normal text-foreground" >
            Bot-Korrekturen
          </h2>
          {corrections && corrections.length > 0 && (
            <Badge className="bg-red-500 text-white">{corrections.length}</Badge>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}
          className="border-border text-primary hover:text-primary">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <p className="text-sm text-muted-foreground" >
        Hier siehst du Bot-Antworten mit niedriger Confidence. Korrigiere sie, damit der Bot dazulernt.
      </p>

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary"  /></div>
      ) : !corrections || corrections.length === 0 ? (
        <Card className="border-border bg-card">
          <CardContent className="p-8 text-center">
            <Check className="w-12 h-12 mx-auto mb-3 text-green-500" />
            <p className="font-medium text-foreground" >Alles erledigt!</p>
            <p className="text-sm mt-1 text-muted-foreground" >Keine ausstehenden Korrekturen.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {corrections.map((c) => (
            <Card key={c.id} className="border-border bg-card">
              <CardContent className="p-4 space-y-3">
                {/* Original Question */}
                <div>
                  <p className="text-[10px] font-medium mb-1 text-muted-foreground" >Frage des Nutzers</p>
                  <p className="text-sm p-2 rounded bg-background text-foreground">{c.originalQuestion}</p>
                </div>

                {/* Original Bot Answer */}
                <div>
                  <p className="text-[10px] font-medium mb-1 text-muted-foreground" >Bot-Antwort (unsicher)</p>
                  <p className="text-sm p-2 rounded border border-red-200 bg-[#fef2f215] text-foreground">{c.originalAnswer}</p>
                </div>

                {/* Correction Input */}
                {editingId === c.id ? (
                  <div className="space-y-2">
                    <p className="text-[10px] font-medium text-primary" >Korrigierte Antwort</p>
                    <Textarea value={correctedAnswer} onChange={(e) => setCorrectedAnswer(e.target.value)}
                      placeholder="Die richtige Antwort eingeben..."
                      className="bg-background border-border text-foreground min-h-[80px]" />
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={() => { setEditingId(null); setCorrectedAnswer(""); }}
                        className="border-border text-primary">Abbrechen</Button>
                      <Button size="sm" className="bg-red-500 text-white hover:bg-red-600"
                        onClick={() => updateMutation.mutate({ correctionId: c.id, correctedAnswer: "", status: "rejected", correctedBy: "admin" })}
                        disabled={updateMutation.isPending}>
                        Ablehnen
                      </Button>
                      <Button size="sm" className="bg-primary text-white hover:bg-primary/90"
                        onClick={() => updateMutation.mutate({ correctionId: c.id, correctedAnswer, status: "corrected", correctedBy: "admin" })}
                        disabled={updateMutation.isPending || !correctedAnswer.trim()}>
                        {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                        Korrektur speichern
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    {c.correctedAnswer && (
                      <div className="flex-1">
                        <p className="text-[10px] font-medium mb-1 text-primary" >Korrigierte Antwort</p>
                        <p className="text-sm p-2 rounded bg-primary/10 text-foreground">{c.correctedAnswer}</p>
                      </div>
                    )}
                    {c.status === "pending" && (
                      <Button variant="outline" size="sm"
                        onClick={() => { setEditingId(c.id); setCorrectedAnswer(c.correctedAnswer || ""); }}
                        className="border-primary/30 text-primary hover:bg-primary/10 self-end">
                        Korrigieren
                      </Button>
                    )}
                    {c.status === "corrected" && (
                      <Button size="sm" className="bg-green-600 text-white hover:bg-green-700 self-end"
                        onClick={() => updateMutation.mutate({ correctionId: c.id, correctedAnswer: c.correctedAnswer || "", status: "approved", correctedBy: "admin" })}
                        disabled={updateMutation.isPending}>
                        <Check className="w-4 h-4 mr-1" />
                        Genehmigen
                      </Button>
                    )}
                  </div>
                )}

                {/* Status Badge */}
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className={`text-[10px] ${
                    c.status === "pending" ? "border-yellow-500/40 text-yellow-600" :
                    c.status === "corrected" ? "border-blue-500/40 text-blue-600" :
                    c.status === "approved" ? "border-green-500/40 text-green-600" :
                    "border-red-500/40 text-red-600"
                  }`}>
                    {c.status === "pending" ? "Ausstehend" : c.status === "corrected" ? "Korrigiert" : c.status === "approved" ? "Genehmigt" : "Abgelehnt"}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground" >
                    {new Date(c.createdAt).toLocaleDateString("de-DE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  TAB 5: BUG REPORTS
// ═══════════════════════════════════════════════════════════

interface BugReport {
  id: number;
  clientId: string | null;
  clientName: string | null;
  title: string;
  description: string;
  page: string | null;
  screenshots: unknown;
  chatHistory: string | null;
  priority: string;
  status: string;
  adminNotes: string | null;
  userAgent: string | null;
  createdAt: string | Date;
  updatedAt: string | Date;
}

/** Helper: Parse severity from description or priority field */
function parseSeverity(report: BugReport): "low" | "medium" | "high" | "critical" {
  const p = report.priority?.toLowerCase();
  if (p === "critical" || p === "high" || p === "medium" || p === "low") return p;
  // Fallback: parse from description markdown
  const match = report.description?.match(/\*\*Schweregrad:\*\*\s*(\w+)/);
  if (match) {
    const s = match[1].toLowerCase();
    if (s === "critical" || s === "high" || s === "medium" || s === "low") return s;
  }
  return "medium";
}

/** Helper: Parse structured fields from description */
function parseReportFields(report: BugReport) {
  const desc = report.description || "";
  // Try to extract structured fields from markdown format
  const severityMatch = desc.match(/\*\*Schweregrad:\*\*\s*([^\n*]+)/);
  const pageMatch = desc.match(/\*\*Betroffene Seite:\*\*\s*([^\n*]+)/);
  const shortMatch = desc.match(/\*\*Kurzbeschreibung:\*\*\s*([^\n*]+)/);
  const detailMatch = desc.match(/\*\*Detaillierte Beschreibung:\*\*\s*([\s\S]*)$/);
  
  if (shortMatch || detailMatch) {
    return {
      isStructured: true,
      shortDescription: shortMatch?.[1]?.trim() || report.title,
      detailedDescription: detailMatch?.[1]?.trim() || "",
      affectedPage: pageMatch?.[1]?.trim() || report.page || "",
    };
  }
  // Not structured — use raw fields
  return {
    isStructured: false,
    shortDescription: report.title,
    detailedDescription: desc,
    affectedPage: report.page || "",
  };
}

const SEVERITY_CONFIG: Record<string, { label: string; icon: typeof Info; color: string; bg: string; border: string }> = {
  low: { label: "Gering", icon: Info, color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200" },
  medium: { label: "Mittel", icon: AlertCircle, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
  high: { label: "Hoch", icon: AlertTriangle, color: "text-orange-700", bg: "bg-orange-50", border: "border-orange-200" },
  critical: { label: "Kritisch", icon: Flame, color: "text-red-700", bg: "bg-red-50", border: "border-red-200" },
};

function BugReportCard({ report, isOpen, onToggleExpand, isExpanded, onResolve, onReopen, isPending }: {
  report: BugReport;
  isOpen: boolean;
  onToggleExpand: () => void;
  isExpanded: boolean;
  onResolve: () => void;
  onReopen: () => void;
  isPending: boolean;
}) {
  const severity = parseSeverity(report);
  const sevConfig = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.medium;
  const SevIcon = sevConfig.icon;
  const fields = parseReportFields(report);
  const screenshotList = report.screenshots as string[] | null;
  const hasScreenshots = screenshotList && Array.isArray(screenshotList) && screenshotList.length > 0;
  let chatMessages: Array<{role: string; content: string}> = [];
  try {
    if (report.chatHistory) chatMessages = JSON.parse(report.chatHistory);
  } catch { /* ignore */ }

  return (
    <Card className={isOpen ? "border-red-200 bg-red-50/20" : "border-border bg-card"}>
      <CardContent className="p-0">
        {/* Header row — always visible */}
        <button
          onClick={onToggleExpand}
          className="w-full flex items-center gap-3 p-4 text-left hover:bg-black/[0.02] transition-colors rounded-t-xl"
        >
          <ChevronRight className={`w-4 h-4 shrink-0 transition-transform text-muted-foreground ${isExpanded ? "rotate-90" : ""}`} />
          {/* Severity badge */}
          <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${sevConfig.bg} ${sevConfig.color} ${sevConfig.border} border`}>
            <SevIcon className="w-3 h-3" />
            {sevConfig.label}
          </div>
          {/* Status badge */}
          {isOpen ? (
            <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]">Offen</Badge>
          ) : (
            <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px]">Gelöst</Badge>
          )}
          {/* Title */}
          <span className="flex-1 text-sm font-medium truncate text-foreground" >
            {fields.shortDescription}
          </span>
          {/* Meta */}
          <span className="text-xs shrink-0 hidden sm:inline text-muted-foreground" >
            {report.clientName || "Anonym"}
          </span>
          <span className="text-xs shrink-0 text-muted-foreground" >
            {new Date(report.createdAt).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" })}
          </span>
          {/* Quick action button in header */}
          {isOpen ? (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onResolve(); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onResolve(); } }}
              className="shrink-0 ml-1 px-2 py-1 rounded-md text-[10px] font-medium bg-green-100 text-green-700 border border-green-200 hover:bg-green-200 transition-colors cursor-pointer"
            >
              {isPending ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <Check className="w-3 h-3 inline mr-0.5" />}
              Erledigt
            </span>
          ) : (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onReopen(); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onReopen(); } }}
              className="shrink-0 ml-1 px-2 py-1 rounded-md text-[10px] font-medium bg-amber-100 text-amber-700 border border-amber-200 hover:bg-amber-200 transition-colors cursor-pointer"
            >
              {isPending ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <RotateCcw className="w-3 h-3 inline mr-0.5" />}
              Öffnen
            </span>
          )}
        </button>

        {/* Expanded details */}
        {isExpanded && (
          <div className={`px-4 pb-4 pt-0 border-t ${isOpen ? "border-red-200" : "border-border"}`}>
            <div className="mt-3 space-y-3">
              {/* Info grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-background rounded-lg p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-muted-foreground" >Kunde</p>
                  <p className="text-sm text-foreground" >{report.clientName || report.clientId || "Anonym"}</p>
                </div>
                <div className="bg-background rounded-lg p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-muted-foreground" >Betroffene Seite</p>
                  <p className="text-sm font-mono text-foreground" >{fields.affectedPage || "—"}</p>
                </div>
                <div className="bg-background rounded-lg p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-muted-foreground" >Gemeldet am</p>
                  <p className="text-sm text-foreground" >{new Date(report.createdAt).toLocaleString("de-DE")}</p>
                </div>
                <div className="bg-background rounded-lg p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-muted-foreground" >Schweregrad</p>
                  <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${sevConfig.bg} ${sevConfig.color}`}>
                    <SevIcon className="w-3 h-3" />
                    {sevConfig.label}
                  </div>
                </div>
              </div>

              {/* Short description */}
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-muted-foreground" >Kurzbeschreibung</p>
                <p className="text-sm text-foreground" >{fields.shortDescription}</p>
              </div>

              {/* Detailed description */}
              {fields.detailedDescription && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-muted-foreground" >Detaillierte Beschreibung</p>
                  <div className="bg-background rounded-lg p-3">
                    <p className="text-sm whitespace-pre-wrap text-muted-foreground" >{fields.detailedDescription}</p>
                  </div>
                </div>
              )}

              {/* Screenshots */}
              {hasScreenshots && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-muted-foreground" >Screenshots</p>
                  <div className="flex gap-2 flex-wrap">
                    {screenshotList!.map((url: string, i: number) => (
                      <a
                        key={i}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-primary text-xs font-medium hover:bg-[#eef2e6] transition-colors"
                      >
                        <Image className="w-3.5 h-3.5" />
                        Screenshot {i + 1}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Chat history */}
              {chatMessages.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-muted-foreground" >Gesprächsverlauf</p>
                  <div className="bg-background rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                    {chatMessages.map((msg, i) => (
                      <div key={i} className="text-xs">
                        <span className={`font-semibold ${msg.role === "assistant" ? "text-primary" : "text-foreground"}`}>
                          {msg.role === "assistant" ? "Bot:" : "Kunde:"}
                        </span>{" "}
                        <span className="text-muted-foreground">{msg.content}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin notes */}
              {report.adminNotes && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-muted-foreground" >Admin-Notizen</p>
                  <p className="text-sm italic text-muted-foreground" >{report.adminNotes}</p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-2 pt-1">
                {isOpen ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); onResolve(); }}
                    disabled={isPending}
                    className="border-green-300 text-green-700 hover:bg-green-50"
                  >
                    {isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                    Als gelöst markieren
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); onReopen(); }}
                    disabled={isPending}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    {isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
                    Wieder öffnen
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BugReportsTab() {
  const { data: reports, isLoading, refetch } = trpc.bugReport.getAll.useQuery();
  const resolveMutation = trpc.bugReport.updateStatus.useMutation({
    onSuccess: () => { toast.success("Status aktualisiert"); refetch(); },
    onError: (err: any) => toast.error(err.message),
  });
  const [expandedId, setExpandedId] = useState<number | null>(null);

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary"  /></div>;

  const typedReports = (reports ?? []) as unknown as BugReport[];
  const openReports = typedReports.filter((r: BugReport) => r.status === "new" || r.status === "in_progress");
  const resolvedReports = typedReports.filter((r: BugReport) => r.status === "resolved" || r.status === "closed");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-display font-normal text-foreground" >Fehlermelder</h2>
          <p className="text-sm mt-1 text-muted-foreground" >Bug-Reports von Kunden</p>
        </div>
        <div className="flex items-center gap-3">
          {openReports.length > 0 && (
            <Badge className="bg-red-100 text-red-700 border-red-200">
              {openReports.length} offen
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={() => refetch()} className="border-border">
            <RefreshCw className="w-4 h-4 mr-1" /> Aktualisieren
          </Button>
        </div>
      </div>

      {/* Empty state */}
      {openReports.length === 0 && resolvedReports.length === 0 && (
        <Card className="border-border">
          <CardContent className="p-8 text-center">
            <Bug className="w-12 h-12 mx-auto mb-3 text-muted-foreground"  />
            <p className="text-muted-foreground">Noch keine Bug-Reports eingegangen.</p>
          </CardContent>
        </Card>
      )}

      {/* Open Reports */}
      {openReports.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground" >Offene Reports ({openReports.length})</h3>
          {openReports.map((report) => (
            <BugReportCard
              key={report.id}
              report={report}
              isOpen={true}
              isExpanded={expandedId === report.id}
              onToggleExpand={() => setExpandedId(expandedId === report.id ? null : report.id)}
              onResolve={() => resolveMutation.mutate({ reportId: report.id, status: "resolved" })}
              onReopen={() => resolveMutation.mutate({ reportId: report.id, status: "new" })}
              isPending={resolveMutation.isPending}
            />
          ))}
        </div>
      )}

      {/* Resolved Reports */}
      {resolvedReports.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground" >Gelöste Reports ({resolvedReports.length})</h3>
          {resolvedReports.map((report) => (
            <BugReportCard
              key={report.id}
              report={report}
              isOpen={false}
              isExpanded={expandedId === report.id}
              onToggleExpand={() => setExpandedId(expandedId === report.id ? null : report.id)}
              onResolve={() => resolveMutation.mutate({ reportId: report.id, status: "resolved" })}
              onReopen={() => resolveMutation.mutate({ reportId: report.id, status: "new" })}
              isPending={resolveMutation.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  GUTSCHEIN-CODES TAB
// ═══════════════════════════════════════════════════════════

function PromoCodesTab() {
  const { data, isLoading, refetch } = trpc.promo.list.useQuery();
  const [showCreate, setShowCreate] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newAction, setNewAction] = useState<"upgrade_premium" | "discount_50" | "discount_100">("upgrade_premium");
  const [newMax, setNewMax] = useState("");

  const createMutation = trpc.promo.create.useMutation({
    onSuccess: () => {
      toast.success("Gutschein-Code erstellt!");
      setShowCreate(false);
      setNewCode("");
      setNewDesc("");
      setNewMax("");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const toggleMutation = trpc.promo.toggle.useMutation({
    onSuccess: () => {
      toast.success("Status aktualisiert");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const handleCreate = () => {
    if (!newCode.trim()) return;
    createMutation.mutate({
      code: newCode.trim(),
      description: newDesc || undefined,
      action: newAction,
      maxRedemptions: newMax ? parseInt(newMax) : undefined,
    });
  };

  const actionLabels: Record<string, string> = {
    upgrade_premium: "Upgrade auf Premium",
    discount_50: "50% Rabatt",
    discount_100: "100% Rabatt",
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary"  />
      </div>
    );
  }

  const codes = data?.codes || [];
  const redemptions = data?.redemptions || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-light text-foreground" >Gutschein-Codes</h2>
          <p className="text-sm text-muted-foreground" >{codes.length} Codes, {redemptions.length} Einlösungen</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button size="sm" onClick={() => setShowCreate(!showCreate)} className="bg-primary hover:bg-primary/90 text-white">
            <Ticket className="w-4 h-4 mr-1" /> Neuer Code
          </Button>
        </div>
      </div>

      {/* Create Form */}
      {showCreate && (
        <Card className="border-border">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground" >Code *</label>
                <Input
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                  placeholder="z.B. BETA2026"
                  className="mt-1 uppercase font-mono tracking-wider"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground" >Aktion</label>
                <Select value={newAction} onValueChange={(v) => setNewAction(v as typeof newAction)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="upgrade_premium">Upgrade auf Premium</SelectItem>
                    <SelectItem value="discount_50">50% Rabatt</SelectItem>
                    <SelectItem value="discount_100">100% Rabatt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground" >Beschreibung</label>
                <Input
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  placeholder="z.B. Beta-Tester Gutschein"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground" >Max. Einlösungen (leer = unbegrenzt)</label>
                <Input
                  value={newMax}
                  onChange={(e) => setNewMax(e.target.value.replace(/\D/g, ""))}
                  placeholder="z.B. 10"
                  className="mt-1"
                  type="number"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleCreate} disabled={!newCode.trim() || createMutation.isPending} className="bg-primary hover:bg-primary/90 text-white">
                {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Gift className="w-4 h-4 mr-1" />}
                Erstellen
              </Button>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Abbrechen</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Codes List */}
      {codes.length === 0 ? (
        <Card className="border-border">
          <CardContent className="p-8 text-center">
            <Gift className="w-10 h-10 mx-auto mb-3 text-muted-foreground"  />
            <p className="text-sm text-muted-foreground" >Noch keine Gutschein-Codes erstellt</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {codes.map((code) => {
            const codeRedemptions = redemptions.filter((r) => r.promoCodeId === code.id);
            return (
              <Card key={code.id} className={`border-border ${!code.isActive ? "opacity-60" : ""}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-lg font-light tracking-wider text-foreground" >
                          {code.code}
                        </span>
                        <Badge variant={code.isActive ? "default" : "secondary"} className={code.isActive ? "bg-green-100 text-green-700" : ""}>
                          {code.isActive ? "Aktiv" : "Deaktiviert"}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {actionLabels[code.action] || code.action}
                        </Badge>
                      </div>
                      {code.description && (
                        <p className="text-sm text-muted-foreground" >{code.description}</p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground" >
                        <span>Einlösungen: {code.currentRedemptions}{code.maxRedemptions ? `/${code.maxRedemptions}` : ""}</span>
                        {code.expiresAt && <span>Gültig bis: {new Date(code.expiresAt).toLocaleDateString("de-DE")}</span>}
                        <span>Erstellt: {new Date(code.createdAt).toLocaleDateString("de-DE")}</span>
                      </div>
                      {/* Einlösungen */}
                      {codeRedemptions.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-border">
                          <p className="text-xs font-medium mb-1 text-muted-foreground" >Eingelöst von:</p>
                          <div className="flex flex-wrap gap-1">
                            {codeRedemptions.map((r) => (
                              <Badge key={r.id} variant="outline" className="text-xs">
                                {r.clientName || r.clientId} — {new Date(r.redeemedAt).toLocaleDateString("de-DE")}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleMutation.mutate({ id: code.id, isActive: !code.isActive })}
                      disabled={toggleMutation.isPending}
                      className="shrink-0"
                    >
                      {code.isActive ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4 text-gray-400" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  TOOLS TAB — Admin-Werkzeuge
// ═══════════════════════════════════════════════════════════

function ToolsTab() {
  const [voicePref, setVoicePref] = useState<"annam" | "christopher">("annam");
  const [results, setResults] = useState<any>(null);

  const preProduceMutation = trpc.admin.preProduceAllSessions.useMutation({
    onSuccess: (data) => {
      setResults(data);
      toast.success(`${data.generated} Sessions generiert, ${data.cached} bereits im Cache`);
    },
    onError: (err) => {
      toast.error(`Fehler: ${err.message}`);
    },
  });

  return (
    <div className="space-y-6">
      {/* Sessions vorproduzieren */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display font-normal text-foreground" >
            <Music className="w-5 h-5 text-primary"  />
            Sessions vorproduzieren
          </CardTitle>
          <p className="text-sm text-muted-foreground" >
            Generiert alle 14 Session-Typen einmalig und speichert sie im Cache.
            Danach starten Sessions für alle Nutzer sofort — ohne Wartezeit und ohne ElevenLabs-Kosten.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-foreground" >Stimme:</label>
            <div className="flex gap-2">
              <Button
                variant={voicePref === "annam" ? "default" : "outline"}
                size="sm"
                onClick={() => setVoicePref("annam")}
                className={voicePref === "annam" ? "bg-primary hover:bg-primary/90 text-white" : ""}
              >
                Annam
              </Button>
              <Button
                variant={voicePref === "christopher" ? "default" : "outline"}
                size="sm"
                onClick={() => setVoicePref("christopher")}
                className={voicePref === "christopher" ? "bg-primary hover:bg-primary/90 text-white" : ""}
              >
                Christopher
              </Button>
            </div>
          </div>

          <Button
            onClick={() => preProduceMutation.mutate({ voicePreference: voicePref })}
            disabled={preProduceMutation.isPending}
            className="bg-primary hover:bg-primary/90 text-white"
          >
            {preProduceMutation.isPending ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Wird generiert... (kann 5-10 Min dauern)</>
            ) : (
              <><Play className="w-4 h-4 mr-2" /> Alle Sessions generieren ({voicePref})</>
            )}
          </Button>

          {results && (
            <div className="mt-4 space-y-2">
              <div className="flex gap-4 text-sm">
                <span className="flex items-center gap-1 text-primary" >
                  <CheckCircle2 className="w-4 h-4" /> {results.generated} generiert
                </span>
                <span className="flex items-center gap-1 text-muted-foreground" >
                  <Music className="w-4 h-4" /> {results.cached} im Cache
                </span>
                {results.errors > 0 && (
                  <span className="flex items-center gap-1 text-red-500">
                    <XCircle className="w-4 h-4" /> {results.errors} Fehler
                  </span>
                )}
              </div>
              <div className="space-y-1">
                {results.results.map((r: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-xs py-1 px-2 rounded bg-background">
                    {r.status === "generated" && <CheckCircle2 className="w-3 h-3 text-green-600" />}
                    {r.status === "already_cached" && <Music className="w-3 h-3 text-primary"  />}
                    {r.status.startsWith("error") && <XCircle className="w-3 h-3 text-red-500" />}
                    <span className="text-foreground">{r.type}</span>
                    <span className="text-muted-foreground">— {r.status === "already_cached" ? "Bereits im Cache" : r.status === "generated" ? "Neu generiert" : r.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* PWA Update Broadcast */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display font-normal text-foreground" >
            <Smartphone className="w-5 h-5 text-primary"  />
            PWA App-Update Broadcast
          </CardTitle>
          <p className="text-sm text-muted-foreground" >
            Informiert alle aktiven Kunden per E-Mail und Telegram, dass AYU als App installiert werden kann.
          </p>
        </CardHeader>
        <CardContent>
          <PwaBroadcastButton />
        </CardContent>
      </Card>

      {/* Abend-Nachrichten */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display font-normal text-foreground" >
            <Mail className="w-5 h-5 text-primary"  />
            Abend-Nachrichten
          </CardTitle>
          <p className="text-sm text-muted-foreground" >
            Manuell die täglichen Abend-Nachrichten an alle aktiven Kunden senden.
          </p>
        </CardHeader>
        <CardContent>
          <EveningMessageButton />
        </CardContent>
      </Card>
    </div>
  );
}

function PwaBroadcastButton() {
  const [broadcastResult, setBroadcastResult] = useState<any>(null);
  const broadcastMutation = trpc.admin.broadcastPwaUpdate.useMutation({
    onSuccess: (data) => {
      setBroadcastResult(data);
      const totalSent = data.email.sent + data.telegram.sent;
      toast.success(`PWA-Update gesendet: ${totalSent} Nachrichten erfolgreich`);
    },
    onError: (err) => {
      toast.error(`Fehler: ${err.message}`);
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        <Button
          onClick={() => broadcastMutation.mutate({ channels: "all" })}
          disabled={broadcastMutation.isPending}
          className="bg-primary hover:bg-primary/90 text-white"
        >
          {broadcastMutation.isPending ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Wird gesendet...</>
          ) : (
            <><Send className="w-4 h-4 mr-2" /> E-Mail + Telegram senden</>
          )}
        </Button>
        <Button
          variant="outline"
          onClick={() => broadcastMutation.mutate({ channels: "email" })}
          disabled={broadcastMutation.isPending}
          className="border-border"
        >
          <Mail className="w-4 h-4 mr-2" /> Nur E-Mail
        </Button>
        <Button
          variant="outline"
          onClick={() => broadcastMutation.mutate({ channels: "telegram" })}
          disabled={broadcastMutation.isPending}
          className="border-border"
        >
          <Send className="w-4 h-4 mr-2" /> Nur Telegram
        </Button>
      </div>
      {broadcastResult && (
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-background">
            <p className="text-xs font-medium mb-1 text-muted-foreground" >E-Mail</p>
            <p className="text-lg font-light text-primary" >{broadcastResult.email.sent} <span className="text-xs font-normal text-muted-foreground" >/ {broadcastResult.email.total} gesendet</span></p>
            {broadcastResult.email.failed > 0 && <p className="text-xs text-red-500">{broadcastResult.email.failed} fehlgeschlagen</p>}
          </div>
          <div className="p-3 rounded-lg bg-background">
            <p className="text-xs font-medium mb-1 text-muted-foreground" >Telegram</p>
            <p className="text-lg font-light text-primary" >{broadcastResult.telegram.sent} <span className="text-xs font-normal text-muted-foreground" >/ {broadcastResult.telegram.total} gesendet</span></p>
            {broadcastResult.telegram.failed > 0 && <p className="text-xs text-red-500">{broadcastResult.telegram.failed} fehlgeschlagen</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function EveningMessageButton() {
  const triggerMutation = trpc.admin.triggerEveningMessages.useMutation({
    onSuccess: (data) => {
      toast.success(`Abend-Nachrichten gesendet: ${JSON.stringify(data)}`);
    },
    onError: (err) => {
      toast.error(`Fehler: ${err.message}`);
    },
  });

  return (
    <Button
      onClick={() => triggerMutation.mutate()}
      disabled={triggerMutation.isPending}
      className="bg-primary hover:bg-primary/90 text-white"
    >
      {triggerMutation.isPending ? (
        <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Wird gesendet...</>
      ) : (
        <><Mail className="w-4 h-4 mr-2" /> Abend-Nachrichten senden</>
      )}
    </Button>
  );
}

// ═══════════════════════════════════════════════════════════
//  DEMO-ANMELDUNGEN TAB
// ═══════════════════════════════════════════════════════════

function DemoRegistrationsTab() {
  const { data: registrations, isLoading, refetch } = trpc.leads.getDemoRegistrations.useQuery();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary"  />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-display font-normal text-foreground" >
            Demo-Anmeldungen
          </h2>
          <p className="text-sm mt-1 text-muted-foreground" >
            {registrations?.length || 0} Registrierungen über die Demo-Seite
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (!registrations?.length) { toast.error("Keine Daten zum Exportieren"); return; }
              const csv = ["Vorname,Nachname,E-Mail,Telefon,Datum"];
              registrations.forEach((r: any) => {
                csv.push(`"${r.firstName || ""}","${r.lastName || ""}","${r.email || ""}","${r.phone || ""}","${r.createdAt ? new Date(r.createdAt).toLocaleString("de-DE") : ""}"`);
              });
              const blob = new Blob([csv.join("\n")], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `demo-anmeldungen-${new Date().toISOString().slice(0, 10)}.csv`;
              a.click();
              URL.revokeObjectURL(url);
              toast.success(`${registrations.length} Eintr\u00E4ge exportiert`);
            }}
            className="border-border text-primary hover:bg-[#f0f4e8]"
          >
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { refetch(); toast.success("Aktualisiert"); }}
            className="border-border text-primary hover:bg-[#f0f4e8]"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {!registrations?.length ? (
        <Card className="border-border bg-card">
          <CardContent className="p-8 text-center">
            <Play className="w-10 h-10 mx-auto mb-3 text-muted-foreground"  />
            <p className="text-sm text-muted-foreground" >Noch keine Demo-Anmeldungen vorhanden.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background">
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground" >Name</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground" >E-Mail</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground" >Telefon</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground" >Datum</th>
                </tr>
              </thead>
              <tbody>
                {registrations.map((r: any, i: number) => (
                  <tr key={r.id || i} className="border-t border-border">
                    <td className="px-4 py-3 font-medium text-foreground" >
                      {r.firstName} {r.lastName}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground" >
                      <a href={`mailto:${r.email}`} className="hover:underline text-primary" >
                        {r.email}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground" >
                      <a href={`tel:${r.phone}`} className="hover:underline text-primary" >
                        {r.phone || "\u2014"}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground" >
                      {r.createdAt ? new Date(r.createdAt).toLocaleString("de-DE", {
                        day: "2-digit", month: "2-digit", year: "numeric",
                        hour: "2-digit", minute: "2-digit"
                      }) : "\u2014"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
//  MAIN ADMIN PAGE \u2014 8-Tab Navigation
// \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550

type AdminTab = "kunden" | "feedback" | "faqbot" | "korrekturen" | "bugreports" | "gutscheine" | "tools" | "demo" | "fehler" | "mikrobiom" | "event" | "sentry";

const TABS: { id: AdminTab; label: string; icon: typeof Users }[] = [
  { id: "kunden",      label: "Kunden",          icon: Users },
  { id: "demo",        label: "Demo",             icon: Play },
  { id: "feedback",    label: "Feedback",         icon: BarChart3 },
  { id: "faqbot",      label: "FAQ-Bot",          icon: MessageSquare },
  { id: "korrekturen", label: "Korrektur",        icon: AlertTriangle },
  { id: "bugreports",  label: "Bugs",             icon: Bug },
  { id: "gutscheine",  label: "Gutscheine",       icon: Gift },
  { id: "tools",       label: "Tools",            icon: Wrench },
  { id: "fehler",      label: "Monitor",          icon: AlertCircle },
  { id: "sentry",      label: "Fehler-Tracking",  icon: Shield },
  { id: "mikrobiom",   label: "Mikrobiom",        icon: Microscope },
  { id: "event",       label: "Event",            icon: Calendar },
] as const;

export default function AppAdmin() {
  const { user, loading } = useAuth();
  const { client: clientUser, loading: clientLoading } = useClientAuth();
  const [activeTab, setActiveTab] = useState<AdminTab>("kunden");
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Badge counter for corrections
  const { data: corrections } = trpc.faq.getPendingCorrections.useQuery(undefined, {
    refetchInterval: 60000, // Refresh every minute
  });
  const pendingCount = corrections?.filter((c) => c.status === "pending").length ?? 0;

  const isManusAdmin = user && user.role === "admin";
  const isClientAdmin = clientUser && clientUser.isAdmin;
  const isAnyAdmin = isManusAdmin || isClientAdmin;
  const isStillLoading = loading || clientLoading;

  if (isStillLoading) {
    return (
      <div className="flex items-center justify-center py-20 bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAnyAdmin) {
    return (
      <div className="flex items-center justify-center py-20 bg-background">
        <Card className="border-border bg-card max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-primary" />
            <h2 className="text-xl font-display font-normal mb-2 text-foreground">
              Admin-Zugang erforderlich
            </h2>
            <p className="text-sm text-muted-foreground">
              Bitte melde dich mit deinem Admin-Account an, um das Dashboard zu nutzen.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If viewing client detail, show that instead
  if (activeTab === "kunden" && selectedClientId) {
    return (
      <div className="p-4 sm:p-6 max-w-6xl mx-auto">
        <ClientDetail clientId={selectedClientId} onBack={() => setSelectedClientId(null)} />
      </div>
    );
  }

  return (
    <div className="bg-background">
      {/* Tab Navigation */}
      <div className="border-b border-border bg-card sticky top-0 z-10 shadow-sm">
        {/* Mobile (< sm): custom styled dropdown */}
        <div className="sm:hidden px-4 py-3 relative">
          {/* Trigger */}
          <button
            onClick={() => setMobileNavOpen(o => !o)}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-[#f8f8f8] border border-[#d4dcc4] rounded-xl text-sm font-medium text-[#2d3a1e] transition-all hover:border-[#6b7f4e]/40 focus:outline-none"
          >
            <div className="flex items-center gap-2.5">
              {(() => {
                const tab = TABS.find(t => t.id === activeTab);
                if (!tab) return null;
                const Icon = tab.icon;
                return (
                  <>
                    <div className="w-7 h-7 rounded-lg bg-[#6b7f4e]/10 flex items-center justify-center shrink-0">
                      <Icon className="w-3.5 h-3.5 text-[#6b7f4e]" />
                    </div>
                    <span>{tab.label}</span>
                    {tab.id === "korrekturen" && pendingCount > 0 && (
                      <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#6b7f4e] text-white text-[10px] font-bold flex items-center justify-center">
                        {pendingCount}
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
            <ChevronDown className={`w-4 h-4 text-[#8a9680] transition-transform duration-200 shrink-0 ${mobileNavOpen ? "rotate-180" : ""}`} />
          </button>

          {/* Dropdown panel */}
          {mobileNavOpen && (
            <>
              {/* backdrop */}
              <div className="fixed inset-0 z-20" onClick={() => setMobileNavOpen(false)} />
              <div className="absolute left-4 right-4 top-full mt-1 z-30 bg-white rounded-2xl shadow-xl border border-[#d4dcc4] overflow-hidden">
                {TABS.map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => { setActiveTab(tab.id); setSelectedClientId(null); setMobileNavOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                        isActive
                          ? "bg-[#6b7f4e]/10 text-[#6b7f4e] font-medium"
                          : "text-[#5a6b4a] hover:bg-[#f8f8f8]"
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${isActive ? "bg-[#6b7f4e]/20" : "bg-[#f0f3eb]"}`}>
                        <Icon className={`w-3.5 h-3.5 ${isActive ? "text-[#6b7f4e]" : "text-[#8a9680]"}`} />
                      </div>
                      <span className="flex-1 text-left">{tab.label}</span>
                      {tab.id === "korrekturen" && pendingCount > 0 && (
                        <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#6b7f4e] text-white text-[10px] font-bold flex items-center justify-center">
                          {pendingCount}
                        </span>
                      )}
                      {isActive && <div className="w-1.5 h-1.5 rounded-full bg-[#6b7f4e] shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Tablet + Desktop (≥ sm): scrollable tab row.
            Outer element is block+overflow-x-auto so centering works without
            breaking scroll — justify-center on a flex overflow container
            prevents scrolling to the left edge. */}
        <div className="hidden sm:block overflow-x-auto scrollbar-hide">
          <nav className="flex min-w-max mx-auto px-2">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  title={tab.label}
                  onClick={() => { setActiveTab(tab.id); setSelectedClientId(null); }}
                  className={`flex items-center gap-1.5 px-3 lg:px-4 py-3.5 text-[12px] lg:text-[13px] font-light border-b-2 transition-all whitespace-nowrap shrink-0 tracking-wide ${
                    isActive
                      ? "border-primary text-primary font-normal"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:border-border/60"
                  }`}
                >
                  <tab.icon className="w-3.5 h-3.5 shrink-0" />
                  <span className="hidden md:inline">{tab.label}</span>
                  {tab.id === "korrekturen" && pendingCount > 0 && (
                    <span className="bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center leading-none">
                      {pendingCount}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {activeTab === "kunden" && <ClientList onSelectClient={setSelectedClientId} />}
        {activeTab === "feedback" && <FeedbackTab />}
        {activeTab === "faqbot" && <FaqBotTab />}
        {activeTab === "korrekturen" && <CorrectionsTab />}
        {activeTab === "bugreports" && <BugReportsTab />}
        {activeTab === "gutscheine" && <PromoCodesTab />}
        {activeTab === "tools" && <ToolsTab />}
        {activeTab === "demo" && <DemoRegistrationsTab />}
        {activeTab === "fehler" && <ErrorDashboardTab />}
        {activeTab === "sentry" && <AppAdminSentry />}
        {activeTab === "mikrobiom" && <AdminBioTab />}
        {activeTab === "event" && <AdminEventTab />}
      </div>
    </div>
  );
}
